import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { User } from 'src/app/models/user';
import { EstensioneService } from 'src/app/services/estensione.service';
import { ToastrService } from 'ngx-toastr';
import { extensions } from '../config';
import { Estensione } from 'src/app/models/estensione';
import { CompagniaService } from 'src/app/services/compagnia.service';
import { StatoIncaricoService } from 'src/app/services/stato-incarico.service';

@Component({
  selector: 'app-integrazione-reale',
  templateUrl: './integrazione-reale.component.html',
  styleUrls: ['./integrazione-reale.component.scss']
})
export class IntegrazioneRealeComponent implements OnInit {

  slugReale: string = extensions.reale;
  slugItaliana: string = extensions.italiana;
  slugs: string[] = [extensions.reale, extensions.italiana];
  form: FormGroup;
  loggedInUser: User;
  extensionReale: Estensione | null;
  extensionItaliana: Estensione | null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private estensioneService: EstensioneService,
    private toast: ToastrService,
    private compagniaService: CompagniaService,
    private statoIncaricoService: StatoIncaricoService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.loggedInUser = this.authService.loggedInUser.getValue();

    this.form = this.fb.group({
      username: '',
      password: '',
      compagniaReale: '',
      compagniaItaliana: '',
      statoDefault: '',
      enabled: false,
    });

    const [reale, italiana] = await Promise.all([
      this.loadExtension(this.slugReale),
      this.loadExtension(this.slugItaliana),
    ]);

    this.extensionReale = reale;
    this.extensionItaliana = italiana;

    const baseExtension = (reale?.configuration ? reale : null) || (italiana?.configuration ? italiana : null) || reale || italiana;

    if (baseExtension) {
      this.form.patchValue({
        username: baseExtension.configuration?.username ?? '',
        password: baseExtension.configuration?.password ?? '',
        enabled: Boolean(reale?.enabled || italiana?.enabled),
      });
    }

    await Promise.all([
      this.patchCompagniaControl('compagniaReale', reale?.configuration?.compagnia_id),
      this.patchCompagniaControl('compagniaItaliana', italiana?.configuration?.compagnia_id),
    ]);

    const statoDefaultId = reale?.configuration?.default_stato_id ?? italiana?.configuration?.default_stato_id;
    await this.patchStatoDefault(statoDefaultId);
  }

  private async loadExtension(slug: string): Promise<Estensione | null> {
    try {
      return await this.estensioneService.bySlug(slug);
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  private async patchStatoDefault(statoId?: number | string | null) {
    if (!statoId) return;
    try {
      const stato = await this.statoIncaricoService.getOne(statoId as number);
      this.form.patchValue({
        statoDefault: {
          id: stato.id,
          humanIdentifier: stato.humanIdentifier ?? stato.nome ?? stato.id
        }
      });
    } catch (err) {
      console.error(err);
      this.form.patchValue({ statoDefault: statoId });
    }
  }

  private async patchCompagniaControl(controlName: 'compagniaReale' | 'compagniaItaliana', compagniaId?: number | string | null) {
    if (!compagniaId) return;
    try {
      const comp = await this.compagniaService.getOne(compagniaId as number);
      this.form.patchValue({
        [controlName]: comp ? { id: comp.id, humanIdentifier: comp.nome, nome: comp.nome } : compagniaId,
      });
    } catch (err) {
      console.error(err);
      this.form.patchValue({ [controlName]: compagniaId });
    }
  }

  async Salva() {
    const conf = JSON.stringify({
      username: this.form?.value.username,
      password: this.form?.value.password,
    });

    const payloads = [
      {
        slug: this.slugReale,
        configuration: JSON.stringify({
          ...JSON.parse(conf),
          compagnia_id: this.form?.value.compagniaReale?.id ?? this.form?.value.compagniaReale ?? null,
          default_stato_id: this.form?.value.statoDefault?.id ?? this.form?.value.statoDefault ?? null,
        }),
      },
      {
        slug: this.slugItaliana,
        configuration: JSON.stringify({
          ...JSON.parse(conf),
          compagnia_id: this.form?.value.compagniaItaliana?.id ?? this.form?.value.compagniaItaliana ?? null,
          default_stato_id: this.form?.value.statoDefault?.id ?? this.form?.value.statoDefault ?? null,
        }),
      },
    ].map(p => ({
      ...p,
      enabled: !!this.form?.value.enabled,
      studio_id: this.loggedInUser.studio_id,
    }));

    try {
      const results = await Promise.all(
        payloads.map(p => this.estensioneService.saveOrUpdate(p, p.slug))
      );

      const created = results.some(r => r?.created);
      const updated = results.some(r => r?.updated);

      if (!created && !updated) {
        return this.toast.error('Errore nel server');
      }

      const message = created && !updated
        ? 'Configurazioni create'
        : updated && !created
          ? 'Configurazioni salvate'
          : 'Configurazioni Reale/Italiana aggiornate';

      return this.toast.success(message);
    } catch (err) {
      console.error(err);
      return this.toast.error('Impossibile salvare le configurazioni');
    }
  }
}
